import React from 'react'
import { Box, Container, Typography, Grid, Paper, Button, Stack, Link as MuiLink, useMediaQuery, useTheme } from '@mui/material'
import { HeadsetMic, Phone, ArrowForward, Speed, Wifi, Support } from '@mui/icons-material'
import { useLanguage } from '../context/LanguageContext'
import { useContactDetails, toTelHref } from '../hooks/useContactDetails'

/**
 * HelpSection - Support and troubleshooting section
 *
 * This component provides quick access to help resources and contact options.
 * Features support links and direct contact methods for users.
 */
const HelpSection: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { t } = useLanguage()
  const { phone } = useContactDetails()
  
  return (
    <Box component="section" sx={{ 
      bgcolor: 'white', 
      backgroundImage: 'linear-gradient(to top, rgba(25, 118, 210, 0.02) 0%, transparent 100%)',
      py: { xs: 8, md: 8 },
      position: 'relative',
    }}>
      <Container maxWidth="lg">
        <Grid container spacing={6} alignItems="flex-start">
          {/* Left side - Support & Troubleshooting */}
          <Grid item xs={12} md={6}>
            <Stack spacing={0}>
              {/* Section label */}
              <Typography
                sx={{
                  color: '#8A8F98',
                  letterSpacing: '0.3em',
                  textTransform: 'uppercase',
                  fontWeight: 900,
                  fontSize: 18,
                  mb: 1,
                }}
              >
                {t('help.title')}
              </Typography>

              {/* Main heading */}
              <Typography
                component="h2"
                sx={{
                  color: '#0A1929',
                  fontWeight: 800,
                  fontSize: { xs: 32, md: 42 },
                  mb: 2,
                  lineHeight: 1.2
                }}
              >
                {t('help.heading')}
              </Typography>

              {/* Description */}
              <Typography
                sx={{
                  color: '#4A5568',
                  fontSize: { xs: 17, md: 19 },
                  mb: 6,
                  lineHeight: 1.7,
                  maxWidth: 540
                }}
              >
                {t('help.description')}
              </Typography>
              
              {/* Support links with enhanced styling */}
              <Stack spacing={2.5}>
                {[
                  { icon: Wifi, text: t('help.setupGuides'), href: '/support' },
                  { icon: Support, text: t('help.outageStatus'), href: '/support' },
                  { icon: Speed, text: t('help.speedTest'), href: '/speedtest' },
                  { icon: HeadsetMic, text: t('help.contactSupport'), href: '/contact' }
                ].map(({ icon: Icon, text, href }) => (
                  <MuiLink
                    key={text}
                    href={href}
                    sx={{
                      color: '#1976d2',
                      textDecoration: 'none',
                      fontSize: { xs: '1rem', md: '1.05rem' },
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      // Ensure minimum 44px height for touch target
                      minHeight: 44,
                      py: { xs: 1.5, md: 1 },
                      px: 2,
                      borderRadius: 2,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        color: '#1565c0',
                        bgcolor: 'rgba(25, 118, 210, 0.08)',
                        transform: 'translateX(4px)',
                        '& .arrow-icon': {
                          transform: 'translateX(2px)'
                        }
                      },
                      '&:active': {
                        transform: 'translateX(2px) scale(0.98)',
                      },
                    }}
                  >
                    <Icon sx={{ fontSize: 20, color: '#1976d2' }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {text}
                      <ArrowForward className="arrow-icon" sx={{ fontSize: 16, transition: 'transform 0.2s ease' }} />
                    </Box>
                  </MuiLink>
                ))}
              </Stack>
            </Stack>
          </Grid>
          
          {/* Right side - Contact options card */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                bgcolor: 'white',
                borderRadius: 3,
                border: '1px solid #E5E7EB',
                boxShadow: '0 12px 40px rgba(0,0,0,0.1), 0 6px 20px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.03)',
                height: 'fit-content',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)',
                }
              }}
            >
              <Stack spacing={0}>
                {/* Card title */}
                <Typography
                  component="h3"
                  sx={{
                    color: '#1F2937',
                    fontWeight: 700,
                    fontSize: '1.25rem',
                    mb: 1
                  }}
                >
                  {t('help.needHelpNow')}
                </Typography>

                {/* Card description */}
                <Typography
                  sx={{
                    color: '#6B7280',
                    fontSize: '1rem',
                    mb: 3
                  }}
                >
                  {t('help.chatOrCallback')}
                </Typography>
                
                {/* Action buttons with enhanced styling */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button
                    variant="contained"
                    component="a"
                    href={toTelHref(phone)}
                    startIcon={<Phone />}
                    fullWidth={isMobile}
                    sx={{
                      bgcolor: '#1976d2',
                      color: 'white',
                      px: 3,
                      py: { xs: 1.75, md: 1.5 },
                      fontWeight: 700,
                      fontSize: { xs: '0.95rem', md: '0.9rem' },
                      textTransform: 'none',
                      borderRadius: 2,
                      flex: 1,
                      minHeight: { xs: 44, md: 'auto' },
                      transition: 'all 0.2s ease',
                      '&:hover': { bgcolor: '#1565c0', transform: 'translateY(-2px)' },
                      '&:active': { transform: 'translateY(0) scale(0.98)' },
                    }}
                  >
                    {phone}
                  </Button>

                  <Button
                    variant="outlined"
                    component="a"
                    href="/contact"
                    startIcon={<HeadsetMic />}
                    fullWidth={isMobile}
                    sx={{
                      borderColor: '#1976d2',
                      color: '#1976d2',
                      px: 3,
                      py: { xs: 1.75, md: 1.5 },
                      fontWeight: 700,
                      fontSize: { xs: '0.95rem', md: '0.9rem' },
                      textTransform: 'none',
                      borderRadius: 2,
                      flex: 1,
                      borderWidth: 2,
                      minHeight: { xs: 44, md: 'auto' },
                      transition: 'all 0.2s ease',
                      '&:hover': { borderColor: '#1565c0', color: '#1565c0', bgcolor: 'rgba(25,118,210,0.08)', borderWidth: 2, transform: 'translateY(-2px)' },
                      '&:active': { transform: 'translateY(0) scale(0.98)' },
                    }}
                  >
                    Send a message
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}

export default HelpSection
